import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { forkJoin, map, of, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class TrialService {

  constructor(private apiService: ApiService) { }

  getTrialsList(query: string, projectId: string = '', page: number = 0, size: number = 10) {
    const params = new URLSearchParams();
    if (query) {params.set('query', query);}
    if (projectId) {params.set('projectId', projectId);}
    params.set('page', page.toString());
    params.set('size', size.toString());

    return this.apiService.apiGet(`trials?${params.toString()}`);
  }

  getTrialDetails(trialId: string) {
    return this.apiService.apiGet(`trials/${trialId}`);
  }

  findGroups(trialId: string) {
    return this.apiService.apiGet(`groups?trialId=${trialId}`);
  }

  getGroupsList(query: string, trialId: string, page: number = 0, size: number = 10, deviceId?: string) {
    const params = new URLSearchParams();
    if (!trialId) {
      throw new Error('trialId is required to get groups list');
    }

    if (query) {params.set('query', query);}
    if (trialId) {params.set('trialId', trialId);}

    // the flag should only be set to true when the request includes a deviceId. - Ali
    params.set('ongoingConnection', 'false');
    if (deviceId) {
      params.set('deviceId', deviceId);
      params.set('ongoingConnection', 'true');
    }

    params.set('page', page.toString());
    params.set('size', size.toString());

    return this.apiService.apiGet(`groups?${params.toString()}`);
  }

  getDeviceConnection(groupId: string) {
    return this.apiService.apiGet(`groups/${groupId}/connections`);
  }

  getDeviceStatus(deviceId: string) {
    return this.apiService.apiGet(`device-status/${deviceId}`);
  }

  createTrial(trialData: any) {
    return this.apiService.apiPost('trials', trialData);
  }

  createClientTrial(trialData: any) {
    const clientId = 'clientId' in trialData ? trialData.clientId : null;
    const trialId = 'trialId' in trialData ? trialData.trialId : null;
    // if (trialData.clientId) {const clientId = trialData.clientId;}
    // if (trialData.trialId) {let trialId = trialData.trialId;}
    return this.apiService.apiPost(`clients/${clientId}/trials/${trialId}`,trialData);
  }

  updateTrial(trialData: any, trialId: string) {
    return this.apiService.apiPut('trials', trialData, trialId);
  }

  getDeviceConnectionsByTrial(trialId: string, page: number = 0, size: number = 10, getAll: boolean = false) {
    const params = new URLSearchParams();
    if (trialId) { params.set('trialId', trialId); }

    if (getAll) {
      // First get the total count with a minimal request
      return this.apiService.apiGet(`device-connections?${params.toString()}&page=0&size=1`).pipe(
        switchMap((initialResponse: any) => {
          const totalItems = initialResponse.total || 1000;
          // Then get all items in one call
          return this.apiService.apiGet(`device-connections?${params.toString()}&page=0&size=${totalItems}`);
        })
      );
    } else {
      // Normal paginated request
      params.set('page', page.toString());
      params.set('size', size.toString());
      return this.apiService.apiGet(`device-connections?${params.toString()}`);
    }
  }

  getDeviceStatusList(subLevelId: string, page: number = 0, size: number = 1000) {
    const params = new URLSearchParams();
    if (subLevelId) {params.set('subLevelId', subLevelId);}
    params.set('page', page.toString());
    params.set('size', size.toString());
    params.set('sort', 'status,desc');

    return this.apiService.apiGet(`device-status?${params.toString()}`);
  }

  getDevicesByTrialOld(trialId: string, page: number = 0, size: number = 10) {
    // First get paginated device connections
    return this.getDeviceConnectionsByTrial(trialId, page, size).pipe(
      switchMap((connectionsResponse: any) => {
        const deviceIds = connectionsResponse.data.map((item: any) => item.channel.deviceId);

        // If no devices found, return empty array with pagination info
        if (deviceIds.length === 0) {
          return of({
            data: [],
            page: connectionsResponse.page,
            size: connectionsResponse.size,
            total: connectionsResponse.total
          });
        }

        // Get status for all devices in this page
        const deviceStatusCalls = deviceIds.map(deviceId => this.getDeviceStatus(deviceId));
        return forkJoin(deviceStatusCalls).pipe(
          map((statuses: any[]) => ({
            data: statuses,
            page: connectionsResponse.page,
            size: connectionsResponse.size,
            total: connectionsResponse.total
          }))
        );
      })
    );
  }

  getDevicesStatus(subLevelId: string, page: number = 0, size: number = 10, sort: string = 'status,desc') {
    const params = new URLSearchParams();
    if (subLevelId) { params.set('subLevelId', subLevelId); }
    params.set('page', page.toString());
    params.set('size', size.toString());
    params.set('sort', sort);

    return this.apiService.apiGet(`device-status?${params.toString()}`);
  }

  getDeviceCountByTrialId(trialId: string) {
    return this.apiService.apiGet(`trials/${trialId}/summary`);
  }

  stopTrialAPI(stopTs: any, trialId: string) {
    const params = new URLSearchParams();
    if (stopTs) { params.set('stopTs', stopTs); }

    return this.apiService.apiPut(`trials/${trialId}/stop?${params.toString()}`, stopTs);
  }

  stopDeviceAPI(data : any) {
    return this.apiService.apiPut('device-connections/stop-many', data);
  }

  deleteDeviceAPI(ids: string[], idSourceType?: string) {
    const params = new URLSearchParams();
    if (idSourceType) {
      params.set('idSourceType', idSourceType);
    }

    return this.apiService.apiPost(
      `device-connections/delete-many?${params.toString()}`,
      ids // raw string array
    );
  }

  saveNote(noteData: any, trialId: string) {
    return this.apiService.apiPost(`trials/${trialId}/notes` ,noteData);
  }

  getHistoricalData(trialId: string,page: number = 0, size: number = 3,
    sort: string = 'createdTs,desc') {
    const params = new URLSearchParams();
    params.set('page', page.toString());
    params.set('size', size.toString());
    params.set('sort', sort);
    return this.apiService.apiGet(`trials/${trialId}/notes?${params.toString()}`);
  }

  getNoteById(noteId: string, trialId: string) {
    return this.apiService.apiGet(`trials/${trialId}/notes/${noteId}`);
  }

  updateNote(noteData: any, trialId: string, noteId: string) {
    return this.apiService.apiPut(`trials/${trialId}/notes/${noteId}` ,noteData);
  }
}
